import { useReducedMotion } from "framer-motion";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "../../lib/utils";

type SidebarTransitionContextValue = {
  renderedWidth: string | null;
  setRenderedWidth: (width: string) => void;
};

const SidebarTransitionContext =
  createContext<SidebarTransitionContextValue | null>(null);

/**
 * Retains the last rendered desktop sidebar width above the route tree. A new
 * page can therefore mount at the previous page's width before moving to its
 * own target on the next frame.
 */
export function DashboardSidebarTransitionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [renderedWidth, setRenderedWidth] = useState<string | null>(null);
  const value = useMemo(
    () => ({ renderedWidth, setRenderedWidth }),
    [renderedWidth],
  );

  return (
    <SidebarTransitionContext.Provider value={value}>
      {children}
    </SidebarTransitionContext.Provider>
  );
}

function useRouteAwareSidebarWidth(targetWidth: string) {
  const transition = useContext(SidebarTransitionContext);
  const prefersReducedMotion = useReducedMotion() === true;
  const renderedWidth = transition?.renderedWidth ?? targetWidth;

  useLayoutEffect(() => {
    if (!transition) return;

    if (transition.renderedWidth === null) {
      transition.setRenderedWidth(targetWidth);
      return;
    }

    if (transition.renderedWidth === targetWidth) return;

    if (prefersReducedMotion) {
      transition.setRenderedWidth(targetWidth);
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      transition.setRenderedWidth(targetWidth);
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [prefersReducedMotion, targetWidth, transition]);

  return renderedWidth;
}

type DashboardSidebarGridProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "style"
> & {
  sidebarWidth: string;
  style?: CSSProperties;
};

export function DashboardSidebarGrid({
  children,
  className,
  sidebarWidth,
  style,
  ...props
}: DashboardSidebarGridProps) {
  const renderedWidth = useRouteAwareSidebarWidth(sidebarWidth);
  const shellStyle = {
    ...style,
    "--dashboard-sidebar-width": renderedWidth,
  } as CSSProperties;

  return (
    <div
      className={cn(
        "grid w-full grid-cols-1 lg:h-full lg:min-h-0 lg:grid-cols-[var(--dashboard-sidebar-width)_minmax(0,1fr)] lg:items-stretch lg:overflow-hidden lg:border-border motion-safe:transition-[grid-template-columns] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        className,
      )}
      style={shellStyle}
      {...props}
    >
      {children}
    </div>
  );
}
